import axios, { AxiosResponse } from 'axios';
import {
  User,
  UserListParams,
  UserListResponse,
  UserCreateRequest,
  UserUpdateRequest,
  UserStatusRequest,
  UserPermissionsRequest,
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  RefreshTokenRequest,
  FirebaseTokenResponse,
  PermissionGroup,
  PermissionGroupCreateRequest,
  Program,
  ApiResponse,
  SuccessResponse
} from '../types/user';
import type { BaseResponse } from './types';

// API 基礎設定
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const API_VERSION = '/api/v1';

// 建立 axios 實例
export const apiClient = axios.create({
  baseURL: `${API_BASE_URL}${API_VERSION}`,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'Accept': 'application/json'
  },
  timeout: 30000
});

// 請求攔截器 - 自動添加認證 token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 回應攔截器 - 統一錯誤處理
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // 處理 403 權限不足錯誤
    if (error.response?.status === 403) {
      // 使用 antd 的 message 組件顯示錯誤訊息
      const message = (await import('antd')).message;
      message.error('權限不足，請聯繫管理員');
      return Promise.reject(error);
    }
    
    // 如果是 401 錯誤且沒有重試過
    if (
      error.response?.status === 401 &&
      originalRequest?.url !== '/auth/refresh' &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      
      try {
        // 嘗試刷新 token
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await authApi.refreshToken({ refreshToken });
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('refreshToken', response.data.refreshToken);
          
          // 重試原始請求
          originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // 刷新失敗，顯示錯誤訊息並重定向到登入頁
        const message = (await import('antd')).message;
        message.error('登入已過期，請重新登入');
        
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refreshToken');
        
        // 延遲一秒後跳轉，讓使用者看到訊息
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
        
        return Promise.reject(refreshError);
      }
    }
    
    // 如果是 401 但沒有 refreshToken，直接提示重新登入
    if (error.response?.status === 401) {
      const message = (await import('antd')).message;
      message.error('登入已過期，請重新登入');
      
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
      
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    }
    
    return Promise.reject(error);
  }
);

// 1. 認證授權 API
export const authApi = {
  // 1.1 使用者登入
  async login(loginData: LoginRequest): Promise<SuccessResponse<LoginResponse>> {
    const response = await apiClient.post<SuccessResponse<LoginResponse>>('/auth/login', loginData);
    return response.data;
  },

  // 1.2 使用者登出
  async logout(): Promise<SuccessResponse<void>> {
    const response = await apiClient.post<SuccessResponse<void>>('/auth/logout');
    return response.data;
  },

  // 1.3 刷新令牌
  async refreshToken(data: RefreshTokenRequest): Promise<SuccessResponse<LoginResponse>> {
    const response = await apiClient.post<SuccessResponse<LoginResponse>>('/auth/refresh', data);
    return response.data;
  },

  // 1.4 修改密碼
  async changePassword(data: ChangePasswordRequest): Promise<SuccessResponse<void>> {
    const response = await apiClient.post<SuccessResponse<void>>('/auth/password/change', data);
    return response.data;
  },

  // 1.5 取得使用者資訊
  async getProfile(): Promise<SuccessResponse<User>> {
    const response = await apiClient.get<SuccessResponse<User>>('/auth/profile');
    return response.data;
  },

  // 1.6 補發 Firebase Custom Token (page-reload 修復用)
  async firebaseToken(): Promise<BaseResponse<FirebaseTokenResponse>> {
    const response = await apiClient.post<BaseResponse<FirebaseTokenResponse>>('/auth/firebase-token');
    return response.data;
  }
};

// 2. 使用者管理 API
export const userApi = {
  // 2.1 取得使用者清單
  async getUsers(params?: UserListParams): Promise<SuccessResponse<UserListResponse>> {
    const response = await apiClient.get<SuccessResponse<UserListResponse>>('/users', { params });
    return response.data;
  },

  // 2.2 取得單一使用者資料
  async getUser(userId: string): Promise<SuccessResponse<User>> {
    const response = await apiClient.get<SuccessResponse<User>>(`/users/${userId}`);
    return response.data;
  },

  // 2.3 建立使用者
  async createUser(userData: UserCreateRequest): Promise<SuccessResponse<User>> {
    const response = await apiClient.post<SuccessResponse<User>>('/users', userData);
    return response.data;
  },

  // 2.4 更新使用者
  async updateUser(userId: string, userData: UserUpdateRequest): Promise<SuccessResponse<User>> {
    const response = await apiClient.put<SuccessResponse<User>>(`/users/${userId}`, userData);
    return response.data;
  },

  // 2.5 刪除使用者
  async deleteUser(userId: string): Promise<SuccessResponse<void>> {
    const response = await apiClient.delete<SuccessResponse<void>>(`/users/${userId}`);
    return response.data;
  },

  // 2.6 更新使用者狀態
  async updateUserStatus(userId: string, statusData: UserStatusRequest): Promise<SuccessResponse<User>> {
    const response = await apiClient.patch<SuccessResponse<User>>(`/users/${userId}/status`, statusData);
    return response.data;
  }
};

// 3. 權限管理 API
export const permissionApi = {
  // 3.1 取得使用者權限
  async getUserPermissions(userId: string): Promise<SuccessResponse<{ userId: string; userName: string; permissions: any[] }>> {
    const response = await apiClient.get<SuccessResponse<{ userId: string; userName: string; permissions: any[] }>>(`/users/${userId}/permissions`);
    return response.data;
  },

  // 3.2 設定使用者權限
  async setUserPermissions(userId: string, data: UserPermissionsRequest): Promise<SuccessResponse<{ userId: string; userName: string; permissions: any[] }>> {
    const response = await apiClient.put<SuccessResponse<{ userId: string; userName: string; permissions: any[] }>>(`/users/${userId}/permissions`, data);
    return response.data;
  },

  // 3.3 取得使用者有效權限
  async getUserEffectivePermissions(userId: string): Promise<SuccessResponse<any>> {
    const response = await apiClient.get<SuccessResponse<any>>(`/users/${userId}/effective-permissions`);
    return response.data;
  },

  // 3.4 取得權限群組清單
  async getPermissionGroups(params?: { page?: number; limit?: number; search?: string; isActive?: boolean }): Promise<SuccessResponse<{ items: PermissionGroup[]; pagination: any }>> {
    const response = await apiClient.get<SuccessResponse<{ items: PermissionGroup[]; pagination: any }>>('/permission-groups', { params });
    return response.data;
  },

  // 3.5 建立權限群組
  async createPermissionGroup(groupData: PermissionGroupCreateRequest): Promise<SuccessResponse<PermissionGroup>> {
    const response = await apiClient.post<SuccessResponse<PermissionGroup>>('/permission-groups', groupData);
    return response.data;
  },

  // 3.6 取得程式清單
  async getPrograms(params?: { active?: boolean }): Promise<SuccessResponse<Program[]>> {
    const response = await apiClient.get<SuccessResponse<Program[]>>('/programs', { params });
    return response.data;
  }
};

// 工具函數
export const apiUtils = {
  // 檢查是否已登入
  isAuthenticated(): boolean {
    return !!(localStorage.getItem('token') || sessionStorage.getItem('token'));
  },

  // 取得當前使用者 token
  getToken(): string | null {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  },

  // 清除認證資訊
  clearAuth(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
  },

  // 處理 API 錯誤
  handleApiError(error: any): string {
    if (error.response?.data?.error?.message) {
      return error.response.data.error.message;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return '發生未知錯誤';
  }
};

export default {
  auth: authApi,
  user: userApi,
  permission: permissionApi,
  utils: apiUtils
};
